import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import type { SxProps, Theme } from '@mui/material/styles'
import type { ReactElement } from 'react'
import { useCallback, useId, useRef, useState } from 'react'

import { KestrixPill } from '../KestrixPill.tsx'
import { colorTokens } from '../tokens/colors.ts'
import type { CellPill } from './cell-display.types.ts'
import { NEUTRAL_PILL_TONE } from './retrofit-subject-tone.ts'
import { PILL_SX, TonedTablePill } from './TonedTablePill.tsx'

const HOVER_LEAVE_DELAY_MS = 120

const OVERFLOW_BUTTON_SX: SxProps<Theme> = {
  display: 'inline-flex',
  flexShrink: 0,
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'default',
  outline: 'none',
  '&:focus-visible': {
    boxShadow: `0 0 0 2px ${colorTokens.global.primary[200]}`,
    borderRadius: '16px'
  }
}

const POPOVER_PAPER_SX: SxProps<Theme> = {
  padding: '12px 14px',
  borderRadius: '8px',
  border: `1px solid ${colorTokens.global.neutral[100]}`,
  boxShadow:
    '0 8px 24px rgba(29, 27, 32, 0.12), 0 2px 8px rgba(29, 27, 32, 0.08)',
  backgroundColor: colorTokens.semantic.brand.base.white,
  maxWidth: '320px',
  minWidth: '180px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '8px'
}

export type PillOverflowChipProps = {
  readonly hiddenPills: readonly CellPill[]
}

/**
 * `+N` chip. Hover or focus opens a popover of the pills that did not fit.
 */
export const PillOverflowChip = ({
  hiddenPills
}: PillOverflowChipProps): ReactElement => {
  const popoverId = useId()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const leaveTimeoutRef = useRef<number | null>(null)

  const clearLeaveTimeout = useCallback((): void => {
    if (leaveTimeoutRef.current !== null) {
      window.clearTimeout(leaveTimeoutRef.current)
      leaveTimeoutRef.current = null
    }
  }, [])

  const openPopover = useCallback(
    (target: HTMLElement): void => {
      clearLeaveTimeout()
      setAnchorEl(target)
    },
    [clearLeaveTimeout]
  )

  const scheduleClose = useCallback((): void => {
    clearLeaveTimeout()
    leaveTimeoutRef.current = window.setTimeout(() => {
      setAnchorEl(null)
      leaveTimeoutRef.current = null
    }, HOVER_LEAVE_DELAY_MS)
  }, [clearLeaveTimeout])

  const isPopoverOpen = anchorEl !== null

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-describedby={isPopoverOpen ? popoverId : undefined}
        aria-expanded={isPopoverOpen}
        data-testid="property-table-cell-pill-overflow"
        onMouseEnter={(event) => openPopover(event.currentTarget)}
        onMouseOver={(event) => openPopover(event.currentTarget)}
        onMouseLeave={scheduleClose}
        onFocus={(event) => openPopover(event.currentTarget)}
        onBlur={scheduleClose}
        sx={OVERFLOW_BUTTON_SX}
      >
        <KestrixPill
          label={`+${hiddenPills.length}`}
          size="sm"
          selectedColor={NEUTRAL_PILL_TONE.fill}
          selectedBorder={NEUTRAL_PILL_TONE.border}
          sx={PILL_SX}
        />
      </Box>
      <Popper
        id={popoverId}
        open={isPopoverOpen}
        anchorEl={anchorEl}
        placement="top"
        modifiers={[
          { name: 'offset', options: { offset: [0, 8] } },
          {
            name: 'preventOverflow',
            options: { padding: 8, boundary: 'viewport' }
          },
          { name: 'flip', options: { fallbackPlacements: ['bottom', 'right'] } }
        ]}
        sx={{ zIndex: 1300 }}
        onMouseEnter={clearLeaveTimeout}
        onMouseLeave={scheduleClose}
      >
        <Paper
          elevation={0}
          data-testid="property-table-cell-pill-overflow-popover"
          sx={POPOVER_PAPER_SX}
        >
          {hiddenPills.map((pill, index) => (
            <TonedTablePill key={`${pill.label}-${index}`} pill={pill} />
          ))}
        </Paper>
      </Popper>
    </>
  )
}
